// Import necessary modules from Angular and RxJS
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { LOCAL_STORAGE_KEYS } from '@g99-product-web-ui/shared-util-lib';
import { Observable, catchError, throwError } from 'rxjs';

import { SKIP_401_HANDLER, SKIP_AUTH } from './ auth-context';
import { LocalStorageService } from '../../services/helper-service/localStorage.service';
import { SharedUserService } from '../../services/shared-user-service/shared-user.service';
import { ToastService } from '../../services/toast-service/toast.service';

/**
 * Interceptor to handle authentication and route-specific logic for HTTP requests.
 */
export const AuthInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const router = inject(Router);
  const sharedUserService = inject(SharedUserService); // ✅ inject here only once
  const localStorageService = inject(LocalStorageService);
  const toastService = inject(ToastService);

  // Skip if context token is set
  if (req.context.get(SKIP_AUTH)) {
    return next(req);
  }

  // Define routes to skip for the interceptor
  const SKIP_ROUTES: string[] = [
    '/api/public/*',
    '/api/auth',
    '/api/user/auth',
    '/graphs',
    '/g99rag.gogroth.com/*',
  ];

  /**
   * Determines if the current route should be skipped based on predefined patterns.
   * @param url The URL to evaluate.
   * @returns `true` if the route matches a skip pattern; otherwise, `false`.
   */
  const shouldSkipRoute = (url: string): boolean => {
    try {
      // Extract the path from the URL to perform accurate matching
      const urlPath = url.includes('://') ? new URL(url).pathname : url;

      return SKIP_ROUTES.some((route) => {
        if (route.endsWith('*')) {
          const baseRoute = route.slice(0, -1);
          return urlPath.startsWith(baseRoute);
        }
        // Match exact path or path with trailing slash
        return urlPath === route || urlPath === route + '/';
      });
    } catch (e) {
      // Fallback to simple inclusion check if URL parsing fails
      return SKIP_ROUTES.some((route) => url.includes(route));
    }
  };

  // Skip interception if the route matches a skip pattern
  if (shouldSkipRoute(req.url)) {
    return next(req);
  }

  // Add the Authorization header if the request is not skipped
  const token = getToken();

  let headers = req.headers;
  if (token) {
    headers = headers.set('Authorization', `Bearer ${token}`);
  }

  const authorizedRequest = req.clone({ headers });

  const skip401 = req.context.get(SKIP_401_HANDLER);

  return next(authorizedRequest).pipe(
    catchError((error: HttpErrorResponse) =>
      handleHttpError(
        error,
        router,
        sharedUserService,
        localStorageService,
        toastService,
        skip401
      )
    )
  );
};

/**
 * Handles HTTP errors for the application.
 * @param error The HTTP error response.
 * @param router Angular Router instance for navigation.
 * @param skip401 When true, the caller opted out of 401-driven logout/redirect for this request.
 * @returns An observable that throws the error.
 */
const handleHttpError = (
  error: HttpErrorResponse,
  router: Router,
  sharedUserService: SharedUserService,
  localStorageService: LocalStorageService,
  toastService: ToastService,
  skip401: boolean
): Observable<never> => {
  switch (error.status) {
    case 401: {
      if (skip401) {
        break;
      }
      // Store current URL before redirecting
      const currentUrl = router.url;
      if (currentUrl !== '/' && !currentUrl.includes('business-selection')) {
        localStorageService.storeItem(
          LOCAL_STORAGE_KEYS.RETURN_URL,
          currentUrl
        );
      }

      // Clear storage but preserve returnUrl
      const returnUrl = localStorageService.readItem(
        LOCAL_STORAGE_KEYS.RETURN_URL
      );
      localStorageService.clearStorage();
      if (returnUrl) {
        localStorageService.storeItem(LOCAL_STORAGE_KEYS.RETURN_URL, returnUrl);
      }

      router.navigate(['/']);
      break;
    }
    case 403:
      handleForbiddenError(error, router, sharedUserService, toastService);
      break;
    default:
      break;
  }

  return throwError(() => error);
};

/**
 * Retrieves the authentication token from the current user's local storage.
 *
 * The function checks for the presence of a `currentUser` object in local storage,
 * parses it, and extracts the `idToken` if available. If no `currentUser` is found
 * or the `idToken` is absent, the function returns `null`.
 *
 * @returns {string | null} The authentication token if available; otherwise, `null`.
 *
 */
const getToken = (): string | null => {
  const currentUser = localStorage.getItem('currentUser');
  return currentUser ? JSON.parse(currentUser)?.idToken || null : null;
};

/**
 * Handles forbidden (403) errors by checking for specific error messages.
 * @param error The HTTP error response.
 * @param router Angular Router instance for navigation.
 */
const handleForbiddenError = (
  error: HttpErrorResponse,
  router: Router,
  sharedUserService: SharedUserService,
  toastService: ToastService
): Observable<never> => {
  let parsedError: { message?: string } | null = null;

  try {
    let errorContent = error.error;

    if (typeof errorContent === 'string') {
      errorContent = errorContent.replace(/,\s*}/, '}');
      parsedError = JSON.parse(errorContent);
    } else if (errorContent && typeof errorContent === 'object') {
      // HttpClient already parsed the JSON response body into an object
      parsedError = errorContent;
    }
  } catch {
    // Malformed error body — ignore and fall through
  }

  //const userId = sharedUserService.getCurrentUserId();

  if (
    parsedError?.message ===
    'Your password has expired. Please update it to continue accessing your account.'
  ) {
    toastService.showError(parsedError.message);
    router.navigate(['/users/change-password']);
  }

  return throwError(() => error);
};
